import type { NodeRendererProps } from "react-arborist";
import { cn } from "src/shadcn/lib/utils";
import { Loader2 } from "lucide-react";
import type { TreeNodeData } from "../types";
import { useTreeContext } from "../TreeContext";
import { SwitcherIcon as DefaultSwitcherIcon } from "./SwitcherIcon";
import { TreeCheckbox } from "./TreeCheckbox";

export const TreeNode = ({
  node,
  style,
  dragHandle,
}: NodeRendererProps<TreeNodeData>) => {
  const {
    disabled: contextDisabled,
    selectable,
    filterTreeNode,
    loadingKeys,
    checkedKeys,
    halfCheckedKeys,
    onRightClick,
    hasLoadData,
    switcherIcon,
    titleRender,
    blockNode,
    showLine,
    indent,
    checkable,
    onCheckToggle,
  } = useTreeContext();
  const data = node.data;
  const isDisabled = contextDisabled || data.disabled;
  const isNodeSelectable = selectable && data.selectable !== false;
  const isHighlighted = filterTreeNode?.(data) ?? false;
  const isLoading = loadingKeys.has(node.id);
  const isChecked = checkedKeys.has(node.id);
  const isHalfChecked = halfCheckedKeys.has(node.id);

  const handleContextMenu = (e: React.MouseEvent) => {
    if (onRightClick) {
      e.preventDefault();
      onRightClick({ event: e, node: data });
    }
  };

  const isLeafNode = data.isLeaf || (!hasLoadData && node.isLeaf);

  const renderSwitcher = () => {
    if (isLeafNode) {
      return <span className="inline-block size-4" />;
    }

    if (isLoading) {
      return <Loader2 className="size-4 shrink-0 animate-spin" />;
    }

    if (switcherIcon) {
      return typeof switcherIcon === "function"
        ? switcherIcon({ expanded: node.isOpen })
        : switcherIcon;
    }

    return <DefaultSwitcherIcon expanded={node.isOpen} />;
  };

  const renderTitle = () => {
    if (titleRender) return titleRender(data);

    return <span className="truncate">{data.title}</span>;
  };

  return (
    <div
      ref={dragHandle}
      className={cn(
        "group flex items-center gap-1 rounded-sm px-1",
        "cursor-pointer select-none",
        node.isSelected && isNodeSelectable && "bg-primary/10",
        node.isFocused && "ring-ring ring-1 ring-inset",
        isHighlighted && "text-orange-500",
        isDisabled && "pointer-events-none opacity-50",
        blockNode && "w-full",
        node.willReceiveDrop && "bg-primary/5"
      )}
      style={style}
      onContextMenu={handleContextMenu}
    >
      {showLine && <IndentLines indent={indent} level={node.level} />}
      <button
        className="shrink-0 rounded-sm p-0.5 hover:bg-muted"
        tabIndex={-1}
        onClick={e => {
          e.stopPropagation();
          if (!isDisabled) node.toggle();
        }}
      >
        {renderSwitcher()}
      </button>
      {checkable && (
        <TreeCheckbox
          checked={isChecked}
          disabled={!!isDisabled}
          indeterminate={isHalfChecked}
          onChange={() => !isDisabled && onCheckToggle(node.id)}
        />
      )}
      <div className="flex-1 truncate py-1 text-sm">{renderTitle()}</div>
    </div>
  );
};

const IndentLines = ({ level, indent }: { level: number; indent: number }) => (
  <>
    {Array.from({ length: level }, (_, i) => (
      <span
        key={i}
        className="absolute top-0 bottom-0 border-l border-border"
        style={{ left: `${i * indent + indent / 2}px` }}
      />
    ))}
  </>
);
